import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ColorGrid from './ColorGrid';
import { NOTE_PALETTE } from './colorPalette';
import { STICKY_SHAPES } from './stickyShapes';
import { useT } from '../i18n/LanguageContext';
import './PostItNode.css';

// Right-click menu: change shape, change color (grid of base + 4 tints per
// color), or delete the note outright. Portaled to document.body so its
// fixed-position coordinates aren't rescaled by the canvas's own pan/zoom
// transform (mirrors ScenarioTimeline.jsx's ScenarioColorMenu).
function PostItContextMenu({ x, y, shape, onShape, onColor, onDelete, onClose }) {
  const t = useT();
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div ref={ref} className="postit-context-menu" style={{ left: x, top: y }}>
      <div className="postit-context-menu__label">{t('canvas.stickyPopover.shape')}</div>
      <div className="postit-context-menu__shapes">
        {Object.entries(STICKY_SHAPES).map(([key, s]) => (
          <button
            key={key}
            type="button"
            className={`postit-context-menu__shape-btn${shape === key ? ' is-active' : ''}`}
            title={t(`canvas.stickyShapes.${key}`)}
            aria-label={t(`canvas.stickyShapes.${key}`)}
            onClick={() => onShape(key, s)}
          >
            <span
              className="postit-context-menu__shape-swatch"
              style={{ borderRadius: key === 'circle' ? '50%' : '4px', width: key === 'rect' ? 18 : 14 }}
            />
          </button>
        ))}
      </div>
      <div className="postit-context-menu__label">{t('canvas.stickyPopover.color')}</div>
      <ColorGrid colors={NOTE_PALETTE} value={null} onPick={onColor} />
      <button type="button" className="postit-context-menu__delete" onClick={onDelete}>
        {t('canvas.postItMenu.delete')}
      </button>
    </div>,
    document.body
  );
}

const PostItNode = ({ data }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(data.label);
  const [menuPos, setMenuPos] = useState(null);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    if (text !== data.label) {
        data.onNodeUpdate(data.id, text);
    }
    setIsEditing(false);
};

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  const width = data.w || 150;
  const height = data.h || 150;
  const bg = data.bg || '#f4fda1';
  const radius = data.radius || '10px';
  const accent = data.tagColor;

  return (
    <>
      <div
          style={{
              position: 'relative',
              padding: '10px',
              backgroundColor: bg,
              border: '1px solid #e1e1d6',
              borderTop: accent ? `4px solid ${accent}` : '1px solid #e1e1d6',
              color: '#333',
              boxShadow: '0px 8px 16px rgba(40,50,90,0.14)',
              borderRadius: radius,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: `${width}px`,
              height: `${height}px`,
              textAlign: 'center',
              cursor: 'grab',
              fontSize: '12px',
              fontWeight: '500',
              fontFamily: "'DM Sans', sans-serif",
              lineHeight: 1.3,
          }}
          onDoubleClick={handleDoubleClick}
          onContextMenu={data.onNodeUpdate && data.onNodeDelete ? handleContextMenu : undefined}
      >
        {data.tag && !isEditing && (
            <span
                style={{
                    position: 'absolute',
                    top: '6px',
                    left: '8px',
                    fontSize: '9px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: accent || '#888',
                    opacity: 0.9,
                }}
            >
                {data.tag}
            </span>
        )}
        {isEditing ? (
            <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault(); // Prevents a new line when Enter is pressed without Shift
                handleBlur();  // Commit changes and exit editing
              }
            }}
            autoFocus
            style={{
              width: '100%',
              height: '100%',        // Adjust height as needed or use rows attribute
              border: 'none',
              outline: 'none',
              padding: '5px',
              textAlign: 'center',
              fontSize: '12px',
              fontFamily: "arial",
              backgroundColor: 'transparent',
              resize: 'none',        // Prevents resizing the textarea
              overflow: 'auto'       // Adds scrollbars if text overflows
            }}
          />
        ) : (
            <span>{text}</span>
        )}
      </div>
      {menuPos && (
        <PostItContextMenu
          x={menuPos.x}
          y={menuPos.y}
          shape={data.shape}
          onShape={(key, preset) => {
            data.onNodeUpdate(data.id, { shape: key, w: preset.w, h: preset.h, radius: preset.radius });
            setMenuPos(null);
          }}
          onColor={(c) => {
            data.onNodeUpdate(data.id, { bg: c });
            setMenuPos(null);
          }}
          onDelete={() => {
            data.onNodeDelete(data.id);
            setMenuPos(null);
          }}
          onClose={() => setMenuPos(null)}
        />
      )}
    </>
  );
};

export default PostItNode;
